import React from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { User, ChevronDown, LogOut, Eye, PanelLeftClose, PanelLeft, RefreshCw, ChevronRight, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { NotificationBell } from '@/components/notification-bell'
import { useLogout } from '@/hooks/use-auth'
import { useViewRoleStore, getRoleName } from '@/stores/view-role'
import { useSidebarStore } from '@/stores/sidebar'
import { UserRole } from '@/types/menu'
import { menuConfig } from '@/lib/menu/config'
import { getBreadcrumbs, isMenuPath, isTabAccessibleForRole } from '@/lib/menu/utils'
import { useTabbarStore } from '@/stores/tabbar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ToolbarProps {
  /** 当前用户信息 */
  currentUser: {
    name: string
    phone?: string
    avatar?: string | null
    roles: string[]
  }
  /** 未读通知数 */
  unreadCount: number
  /** SSE 连接状态 */
  isConnected: boolean
}

/**
 * 可切换的角色列表（管理员可以切换的视角）
 */
const switchableRoles: { role: UserRole; label: string }[] = [
  { role: 'MARKET', label: '市场部视角' },
  { role: 'SERVICE', label: '客服部视角' },
  { role: 'ENGINEER', label: '工程师（上门）视角' },
  { role: 'LAB_ENGINEER', label: '实验室工程师视角' },
  { role: 'FINANCE', label: '财务部视角' },
  // 客户类视角（CUSTOMER、MONTHLY_ADMIN）已移除：如需以客户身份操作，
  // 请在"客户管理 > 客户用户"列表使用"以该用户身份登录"（模拟登录）功能
]

/**
 * 顶部工具栏组件
 */
export function Toolbar({ currentUser, unreadCount, isConnected }: ToolbarProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { logout } = useLogout()
  const { viewRole, setViewRole } = useViewRoleStore()
  const { collapsed, toggleCollapsed } = useSidebarStore()
  const { filterTabs } = useTabbarStore()

  // 切换角色并跳转到工作台
  const handleRoleSwitch = (role: UserRole | null) => {
    setViewRole(role)
    // 过滤标签页，移除新角色无权访问的标签
    filterTabs((tab) => isTabAccessibleForRole(tab.id, role, menuConfig))
    navigate('/dashboard')
  }

  // 刷新当前页面
  const handleRefresh = () => {
    window.location.reload()
  }

  // 返回上一页
  const handleBack = () => {
    navigate(-1)
  }

  // 判断当前用户是否为管理员
  const isAdmin = currentUser?.roles?.includes('ADMIN')

  // 获取当前视角显示名称
  const currentViewLabel = viewRole ? getRoleName(viewRole) + '视角' : '管理员视角'

  // 生成面包屑
  const breadcrumbs = getBreadcrumbs(menuConfig, pathname)

  // 是否显示返回按钮
  // 1. 当前路径不是菜单中的精确路径（即从菜单跳转到非菜单页面）
  // 2. 或者面包屑多于1级
  const isCurrentPathInMenu = isMenuPath(menuConfig, pathname)
  const showBackButton = !isCurrentPathInMenu || breadcrumbs.length > 1

  return (
    <header className="flex h-12 shrink-0 items-center border-b border-slate-200/60 bg-white/90 backdrop-blur-sm px-4 shadow-sm">
      {/* 左侧：侧边栏切换 + 面包屑 */}
      <div className="flex items-center flex-1 gap-2">
        {/* 侧边栏展开/收缩按钮 */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleCollapsed}
          className="h-8 w-8 rounded-lg hover:bg-slate-100 transition-colors"
          title={collapsed ? '展开侧边栏' : '收起侧边栏'}
        >
          {collapsed ? (
            <PanelLeft className="h-4 w-4 text-slate-600" />
          ) : (
            <PanelLeftClose className="h-4 w-4 text-slate-600" />
          )}
        </Button>

        {/* 分隔线 */}
        <div className="w-px h-5 bg-slate-200" />

        {/* 返回按钮（子页面显示） */}
        {showBackButton && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8 rounded-lg hover:bg-slate-100 transition-colors"
              title="返回"
            >
              <Undo2 className="h-4 w-4 text-slate-600" />
            </Button>
            <div className="w-px h-5 bg-slate-200" />
          </>
        )}

        {/* 面包屑导航 */}
        <nav className="flex items-center text-sm">
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={item.id || item.href || index}>
              {index > 0 && (
                <ChevronRight className="h-3.5 w-3.5 mx-1 text-slate-400" />
              )}
              {index < breadcrumbs.length - 1 && item.href ? (
                <Link
                  to={item.href}
                  className="text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {item.title}
                </Link>
              ) : (
                <span className="text-slate-700 font-medium">{item.title}</span>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* 分隔线 */}
        <div className="w-px h-5 bg-slate-200 ml-2" />

        {/* 刷新按钮 */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          className="h-8 w-8 rounded-lg hover:bg-slate-100 transition-colors"
          title="刷新页面"
        >
          <RefreshCw className="h-4 w-4 text-slate-600" />
        </Button>
      </div>

      {/* 右侧：工具栏 */}
      <div className="flex items-center gap-1 shrink-0">
        {/* 角色视角切换器（仅管理员可见）*/}
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 rounded-lg border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all shrink-0">
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">{currentViewLabel}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white/95 backdrop-blur-sm shadow-soft-lg border-slate-200/60">
              <DropdownMenuItem
                onClick={() => handleRoleSwitch(null)}
                className={cn('cursor-pointer transition-colors', !viewRole && 'bg-blue-50 text-blue-700')}
              >
                <Eye className="mr-2 h-4 w-4" />
                管理员视角
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {switchableRoles.map(({ role, label }) => (
                <DropdownMenuItem
                  key={role}
                  onClick={() => handleRoleSwitch(role)}
                  className={cn('cursor-pointer transition-colors', viewRole === role && 'bg-blue-50 text-blue-700')}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* 通知入口：hover 浮层 + 点击进通知中心 */}
        <NotificationBell unreadCount={unreadCount} />

        {/* SSE 连接状态指示器 */}
        <div
          className={cn(
            'w-2 h-2 rounded-full transition-colors',
            isConnected
              ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] pulse-dot'
              : 'bg-slate-300'
          )}
          title={isConnected ? '实时通知已连接' : '实时通知未连接'}
        />

        {/* 分隔线 */}
        <div className="w-px h-6 bg-slate-200 mx-2" />

        {/* 用户下拉菜单 */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 rounded-lg hover:bg-slate-100 transition-colors">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                <span className="text-white text-xs font-semibold">{currentUser?.name?.charAt(0) || 'U'}</span>
              </div>
              <span className="text-sm font-medium text-slate-700">{currentUser?.name || '用户'}</span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-white/95 backdrop-blur-sm shadow-soft-lg border-slate-200/60">
            <DropdownMenuItem asChild>
              <Link to="/dashboard/profile" className="cursor-pointer transition-colors">
                <User className="mr-2 h-4 w-4" />
                个人中心
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors">
              <LogOut className="mr-2 h-4 w-4" />
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
